import React from 'react';
import { Typography, Alert, Button, Flex, theme } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';

import BottomActionsContainer from './BottomActionsContainer';
import { HubSpot, EVENTS_TRACKED } from '../../CustomerSupport';
import HelpOptionsCard from './HelpOptionsCard';
import VerificationResult from '../VerificationResult';
import { StepCodes, type StepCode } from '../steps';
import { nexusReferralState } from '../nexusReferral';
import { useEvent } from '~features/events/hooks/useEvent';

const { Paragraph, Text } = Typography;

function formatDomainForDisplay(url: string | undefined): string {
  if (!url) return 'this domain';
  try {
    const parsed = new URL(url);
    return parsed.hostname || url;
  } catch {
    return url;
  }
}

export const integrationErrorMap: Record<number, JSX.Element> = {
  0: (
    <Typography.Paragraph>
      We couldn&apos;t fetch your page. It probably has nothing to do with your Prerender integration. It can happen when
      your site doesn&apos;t respond with an HTTP200, is not available publicly on the Internet, or has some other type of
      traffic filtering. Please adjust your site&apos;s settings and try again.
    </Typography.Paragraph>
  ),
  1: (
    <div>
      <Typography.Paragraph>
        Looks like the integration is not working as expected. Failures could be caused by the following reasons:{' '}
      </Typography.Paragraph>
      <ul>
        <li>Integration misconfiguration</li>
        <li>IP restrictions on your website</li>
        <li>Bot restrictions on your website</li>
        <li>Using Cache solution</li>
      </ul>
      <Typography.Paragraph>We recommend that you try one of the following steps:</Typography.Paragraph>
      <ul>
        <li>Check your integration</li>
        <li>
          Whitelist our service IPs that you can find{' '}
          <Typography.Link href="https://docs.prerender.io/docs/22-ip-addresses" target="_blank" rel="noreferrer">
            here
          </Typography.Link>
        </li>
        <li>Check your CDN configuration</li>
      </ul>
    </div>
  ),
  2: (
    <Typography.Paragraph>
      We couldn&apos;t fetch your page. It probably has nothing to do with your Prerender integration. It can happen when
      your site doesn&apos;t respond with an HTTP200, is not available publicly on the Internet, or has some other type of
      traffic filtering. Please adjust your site&apos;s settings and try again.
    </Typography.Paragraph>
  ),
  4: (
    <div>
      <Typography.Paragraph>Please verify that you&apos;re using the correct token.</Typography.Paragraph>
    </div>
  ),
};

function VerificationError({
  jumpToStep,
}: {
  jumpToStep: (stepCode: StepCode, state?: Record<string, unknown>) => void
}) {
  const { token } = theme.useToken();
  const location = useLocation();
  const navigate = useNavigate();
  const { track } = useEvent();

  const validationResult = location.state?.validationResult as VerificationResult;
  const url = location.state?.url as string | undefined;
  const domainDisplay = formatDomainForDisplay(url);

  const onIntegrationGuideClick = () => {
    jumpToStep(StepCodes.Integrate);
  };
  const onContinueWithNexusClick = () => {
    jumpToStep(StepCodes.Integrate, nexusReferralState('verify_step'));
  };
  const onScheduleCallClick = () => {
    HubSpot.openHubSpotForm();
    track(EVENTS_TRACKED.contactSupportClicked)

  };

  return (
    <Flex gap="large" vertical flex={1} align="stretch" style={{ width: '100%' }}>
      <Alert
        style={{ width: '100%' }}
        message={
          <Paragraph style={{ fontSize: token.fontSizeLG }}>
            Integration for <Text strong style={{ fontSize: token.fontSizeLG }}>{domainDisplay}</Text> not detected
          </Paragraph>
        }
        type="error"
        showIcon
        description={
          <div style={{ maxWidth: token.screenMD }}>
            {validationResult?.errors?.length
            ? integrationErrorMap[validationResult?.errors[0]]
            : 'We reached your site but can’t detect an integration.'
            }
          </div>
        }
      />

      <HelpOptionsCard
        onIntegrationGuideClick={onIntegrationGuideClick}
        onContinueWithNexusClick={onContinueWithNexusClick}
        onScheduleCallClick={onScheduleCallClick}
      />
      <BottomActionsContainer
        leftElement={
          <Button
            onClick={() => navigate(`/integration-wizard/steps/verify-integration`)}
            icon={<LeftOutlined />}
            type="text"
          >
            Verify Integration
          </Button>
        }
      />
    </Flex>
  );
}

export default VerificationError;
